import axios from 'axios';
import {initialize} from 'launchdarkly-react-client-sdk';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import * as EnvironmentConstants from './constants/environment-constants';

import type {LDClient, LDContext, LDOptions} from 'launchdarkly-react-client-sdk';

const getLaunchDarklyContext = (simCaptureEnvironment) => {
    if (simCaptureEnvironment === 'local') {
        return {
            kind: 'user',
            key: __UI_LAUNCHDARKLY_CONTEXT_KEY__,
        };
    } else if (window.location.host.split('.')[1]) {
        return {
            kind: 'user',
            key: window.location.host.split('.')[0],
        };
    } else {
        throw new Error('Non-local environments must have a subdomain');
    }
};

type LaunchDarklyProviderConfig = {
    clientSideID: string;
    context: LDContext;
    options?: LDOptions;
};

export const getLaunchDarklyProviderConfig = (environment: string): LaunchDarklyProviderConfig => {
    const launchDarklyClientSideId = EnvironmentConstants.LAUNCH_DARKLY_CLIENT_IDS[environment];

    if (isNil(launchDarklyClientSideId)) {
        throw new Error(`Unable to set LaunchDarkly clientSideID with environment: ${environment}`);
    }

    return {
        clientSideID: launchDarklyClientSideId,
        context: getLaunchDarklyContext(environment),
    };
};

let ldClient: LDClient;

export const getAllFlags = () => ldClient.allFlags();

export const setupLDClient = async (launchDarklyProviderConfig: LaunchDarklyProviderConfig) => {
    ldClient = initialize(launchDarklyProviderConfig.clientSideID, launchDarklyProviderConfig.context);
    await ldClient.waitForInitialization(5).catch(async (reason) => {
        console.error('LaunchDarkly client failed to initialize', reason);
        if (!isEmpty(__LAUNCH_DARKLY_PROXY_URI__)) {
            // eslint-disable-next-line no-console
            console.log('Trying to connect to LaunchDarkly proxy');
            launchDarklyProviderConfig.options = {
                baseUrl: `${__LAUNCH_DARKLY_PROXY_URI__}/base`,
                streamUrl: `${__LAUNCH_DARKLY_PROXY_URI__}/stream`,
                eventsUrl: `${__LAUNCH_DARKLY_PROXY_URI__}/events`,
                streaming: true,
            };
            ldClient = initialize(
                launchDarklyProviderConfig.clientSideID,
                launchDarklyProviderConfig.context,
                launchDarklyProviderConfig.options,
            );
            await ldClient
                .waitForInitialization(5)
                .then(() => axios.post(`${__API_HOST__}/launchdarkly/proxy/success`))
                .catch((reasonProxy) => {
                    console.error('LaunchDarkly proxy failed to initialize', reasonProxy);
                    axios.post(`${__API_HOST__}/launchdarkly/proxy/failure`);
                });
        }
    });

    return ldClient;
};
